import type { FeatureCollection, Point, Polygon } from "geojson";
import type { GeoJSONSource, Map as FleetGLMap } from "maplibre-gl";
import { Facility, FleetVehicle, validPosition } from "./fleetData";
import { fleetDestination } from "./fleetRoute";
import type { FleetLabelFields } from "./useFleetEstimates";

export const fleetColors = { running: "#287352", idle: "#996322", halted: "#ae4141", unknown: "#56677e", stale: "#6b7280" };
export const fleetSource = "fleet-vehicles";
export const clusterLayer = "fleet-clusters";
export const vehicleLayer = "fleet-trucks";
export const plateLayer = "fleet-plates";
export const modelSource = "fleet-trailer-models";
export const modelLayer = "fleet-trailers-3d";
export const routeSource = "fleet-road-route";
export const baseRouteSource = "fleet-base-routes";
export const emptyCollection: FeatureCollection = { type: "FeatureCollection", features: [] };
const plateTextSize = 11.5;
const plateLineHeight = 1.05;
const batteryTextSize = 10;

// These are screen-sized GPS symbols, not surveyed vehicle footprints. Native
// extrusion geometry gives the symbols real roofs and shaded vertical faces.
export function trailerModelFeatures(vehicles: FleetVehicle[], zoom: number, visibleIds: Set<string>): FeatureCollection<Polygon> {
  const features: FeatureCollection<Polygon>["features"] = [];
  vehicles.forEach(vehicle => {
    const position = validPosition(vehicle.lat, vehicle.lng);
    if (!position || !visibleIds.has(vehicle.id)) return;
    const cosine = Math.cos(position.lat * Math.PI / 180);
    const metresPerPixel = 40075016.686 * cosine / (512 * 2 ** zoom);
    const rawHeading = vehicle.telemetry?.heading;
    const heading = typeof rawHeading === "number" && Number.isFinite(rawHeading) ? rawHeading * Math.PI / 180 : 0;
    const color = vehicle.unreachable ? fleetColors.stale : fleetColors[vehicle.status];
    const point = (x: number, y: number): number[] => {
      const east = x * Math.cos(heading) + y * Math.sin(heading);
      const north = -x * Math.sin(heading) + y * Math.cos(heading);
      return [position.lng + east * metresPerPixel / (111320 * cosine), position.lat + north * metresPerPixel / 111320];
    };
    const part = (outline: number[][], base: number, height: number, fill: string) => {
      const ring = outline.map(([x, y]) => point(x, y));
      ring.push(ring[0]);
      features.push({ type: "Feature", geometry: { type: "Polygon", coordinates: [ring] },
        properties: { id: vehicle.id, color: fill, base: base * metresPerPixel, height: height * metresPerPixel } });
    };
    const box = (x: number, y: number, width: number, length: number, base: number, height: number, fill: string) =>
      part([[x-width/2,y-length/2],[x+width/2,y-length/2],[x+width/2,y+length/2],[x-width/2,y+length/2]],base,height,fill);
    // Chassis, rear trailer, cab and a blue windshield strip.
    box(0, 0, 11, 33, 1.5, 3, "#304356");
    box(0, -4.5, 12, 24, 3, 11, color);
    box(0, 13, 12.5, 8, 2, 8, "#eef1f4");
    box(0, 15.5, 10, 2.3, 8, 8.3, "#b8dfed");
    // A narrow roof highlight makes the trailer's height legible at fleet zoom.
    box(-3.8, -4.5, .8, 22, 11, 11.15, "#dfeee7");
    for (const x of [-6.2, 6.2]) for (const y of [-12, -8, 11.5]) {
      const outline = Array.from({length:12},(_,i) => [x+1.9*Math.cos(i*Math.PI/6), y+2.5*Math.sin(i*Math.PI/6)]);
      part(outline, .4, 2.5, "#243442");
    }
  });
  return { type: "FeatureCollection", features };
}

export function refreshTrailerModels(map: FleetGLMap, vehicles: FleetVehicle[]) {
  const source = map.getSource(modelSource) as GeoJSONSource | undefined;
  if (!source) return;
  const visible = map.querySourceFeatures(fleetSource, { filter: ["!", ["has", "point_count"]] });
  const ids = new Set(visible.map(feature => String(feature.properties.id)));
  source.setData(trailerModelFeatures(vehicles, map.getZoom(), ids));
}

export function vehicleFeatures(vehicles: FleetVehicle[], selectedId?: string, metrics: Record<string,string> = {}, details: Record<string,FleetLabelFields> = {}): FeatureCollection<Point> {
  return { type: "FeatureCollection", features: vehicles.flatMap(vehicle => {
    const point = validPosition(vehicle.lat, vehicle.lng);
    if (!point) return [];
    // Both banner foot and truck cab share the GPS anchor; never fan labels
    // away from their vehicle when neighbouring trailers are close together.
    const bannerOffset=[0,0];
    const status = vehicle.unreachable ? "stale" : vehicle.status;
    const heading = vehicle.telemetry?.heading;
    const plateCharacters = Array.from(vehicle.plate.replace(/\s+/g,""));
    // Viewport offsets use text ems. Leave room for the banner padding,
    // a 6px gap and the battery badge's bottom padding above the number.
    const batteryOffset = [0, -(plateCharacters.length * plateTextSize * plateLineHeight + 4 + 6 + 3) / batteryTextSize];
    const battery = vehicle.telemetry?.battery;
    const batteryValid = typeof battery === "number" && Number.isFinite(battery) && battery>=0 && battery<=100 && !vehicle.telemetry?.metricsStale && !vehicle.telemetry?.stale && !vehicle.unreachable;

    return [{ type: "Feature" as const, id: vehicle.id, geometry: { type: "Point" as const, coordinates: [point.lng, point.lat] },
      properties: { id: vehicle.id, plate: vehicle.plate, metrics: metrics[vehicle.id] || "", status, color: fleetColors[status],
        bannerOffset,
        batteryLabel:batteryValid?`${Math.round(battery!)}%`:"—",
        batteryColor:!batteryValid?"#64748b":battery!<=20?"#b42318":battery!<=40?"#996322":"#287352",
        batteryOffset,
        verticalPlate:plateCharacters.join("\n"),
        speedLabel: details[vehicle.id]?.speed || "— km/h", distanceLabel: details[vehicle.id]?.distance || "— km", timeLabel: details[vehicle.id]?.time || "— min",
        heading: typeof heading === "number" && Number.isFinite(heading) ? ((heading % 360) + 360) % 360 : 0,
        selected: vehicle.id === selectedId, priority: vehicle.id === selectedId ? 0 : 1 } }];
  }) };
}

export function destinationFeatures(vehicles: FleetVehicle[], facilities: Facility[]): FeatureCollection<Point> {
  const seen = new Set<string>();
  return { type: "FeatureCollection", features: vehicles.flatMap(vehicle => {
    const endpoint = fleetDestination(vehicle, facilities);
    if (!endpoint) return [];
    const key = `${endpoint.kind}:${endpoint.lat}:${endpoint.lng}`;
    if (seen.has(key)) return [];
    seen.add(key);
    return [{ type: "Feature" as const, geometry: { type: "Point" as const, coordinates: [endpoint.lng, endpoint.lat] },
      properties: { name: endpoint.name, kind: endpoint.kind } }];
  }) };
}

function truckImage(color: string): ImageData | undefined {
  const canvas = document.createElement("canvas");
  canvas.width = 56; canvas.height = 80;
  const context = canvas.getContext("2d");
  if (!context) return;
  // Pixel ratio 2 keeps the trailer sharp on retina displays. The cab faces north.
  context.shadowColor = "#18334c55"; context.shadowBlur = 5; context.shadowOffsetY = 3;
  context.fillStyle = "#23364a";
  context.fillRect(11, 18, 5, 12); context.fillRect(40, 18, 5, 12);
  context.fillRect(11, 51, 5, 13); context.fillRect(40, 51, 5, 13);
  context.fillStyle = "#fff"; context.fillRect(14, 11, 28, 56);
  context.shadowColor = "transparent";
  context.fillStyle = color; context.fillRect(17, 31, 22, 33);
  context.fillStyle = "#eef1f4"; context.fillRect(17, 14, 22, 13);
  context.fillStyle = "#dceef3"; context.fillRect(20, 16, 16, 5);
  context.fillStyle = "#ffffff50"; context.fillRect(20, 34, 3, 27);
  return context.getImageData(0, 0, canvas.width, canvas.height);
}

function numberBannerImage(): ImageData | undefined {
  const canvas=document.createElement("canvas");canvas.width=64;canvas.height=112;
  const context=canvas.getContext("2d");if(!context) return;
  context.shadowColor="#18334c35";context.shadowBlur=5;context.shadowOffsetX=2;context.shadowOffsetY=3;
  context.fillStyle="#fff";context.fillRect(8,6,42,92);context.shadowColor="transparent";
  const face=context.createLinearGradient(8,0,50,0);
  face.addColorStop(0,"#f1f4f6");face.addColorStop(.25,"#fff");face.addColorStop(.8,"#fff");face.addColorStop(1,"#e4e9ed");
  context.fillStyle=face;context.fillRect(8,6,42,92);
  // A narrow side and bottom face give the label depth without a large card.
  context.fillStyle="#aab8c4";context.beginPath();context.moveTo(50,6);context.lineTo(56,10);context.lineTo(56,102);context.lineTo(50,98);context.closePath();context.fill();
  context.fillStyle="#c7d1da";context.beginPath();context.moveTo(8,98);context.lineTo(50,98);context.lineTo(56,102);context.lineTo(14,102);context.closePath();context.fill();
  context.fillStyle="#ffffffdd";context.fillRect(9,7,1,90);
  return context.getImageData(0,0,64,112);
}

function batteryBadgeImage(): ImageData | undefined {
  const canvas=document.createElement("canvas");canvas.width=64;canvas.height=40;
  const context=canvas.getContext("2d");if(!context) return;
  context.fillStyle="#ffffffa6";context.fillRect(4,4,52,32);
  context.strokeStyle="#536477";context.lineWidth=2;context.strokeRect(4,4,52,32);
  context.fillStyle="#536477";context.fillRect(57,14,4,12);
  return context.getImageData(0,0,64,40);
}

export function addFleetLayers(map: FleetGLMap) {
  Object.entries(fleetColors).forEach(([status, color]) => {
    const image = truckImage(color);
    if (image) map.addImage("fleet-truck-" + status, image, { pixelRatio: 2 });
  });
  const battery=batteryBadgeImage();
  if(battery) map.addImage("fleet-battery-badge",battery,{pixelRatio:2,stretchX:[[12,48]],stretchY:[[10,30]],content:[8,8,52,32]});
  const banner=numberBannerImage();
  if(banner) map.addImage("fleet-number-banner",banner,{pixelRatio:2,stretchX:[[20,40]],stretchY:[[20,88]],content:[8,6,50,98]});
  map.addSource(fleetSource, { type: "geojson", data: emptyCollection, cluster: true, clusterMaxZoom: 16, clusterRadius: 48 });
  map.addSource("fleet-destinations", { type: "geojson", data: emptyCollection });
  map.addSource(routeSource, { type: "geojson", data: emptyCollection });
  map.addSource(baseRouteSource,{type:"geojson",data:emptyCollection});
  map.addSource(modelSource, { type: "geojson", data: emptyCollection });
  map.addLayer({id:"fleet-base-road-casing",type:"line",source:baseRouteSource,layout:{"line-cap":"round","line-join":"round"},paint:{"line-color":"#fff","line-width":6,"line-opacity":.7}});
  map.addLayer({id:"fleet-base-road-line",type:"line",source:baseRouteSource,layout:{"line-cap":"round","line-join":"round"},paint:{"line-color":"#3576bd","line-width":3,"line-opacity":.8}});
  map.addLayer({ id: "fleet-road-casing", type: "line", source: routeSource, layout: { "line-cap": "round", "line-join": "round" }, paint: { "line-color": "#fff", "line-width": 8, "line-opacity": .9 } });
  map.addLayer({ id: "fleet-road-line", type: "line", source: routeSource, layout: { "line-cap": "round", "line-join": "round" }, paint: { "line-color": "#3576bd", "line-width": 4, "line-opacity": .95 } });
  map.addLayer({ id: "fleet-destination-points", type: "circle", source: "fleet-destinations", paint: { "circle-radius": ["case",["==",["get","kind"],"base"],3,6], "circle-color": ["case", ["==", ["get", "kind"], "base"], "#77669b", "#3576bd"], "circle-stroke-width": 2, "circle-stroke-color": "#fff" } });
  map.addLayer({ id: "fleet-destination-names", type: "symbol", source: "fleet-destinations", layout: { "text-field": ["get", "name"], "text-font": ["Noto Sans Regular"], "text-size": 11, "text-anchor": "left", "text-offset": [1, 0], "text-max-width": 16 }, paint: { "text-color": "#64748b", "text-halo-color": "#fff", "text-halo-width": 2 } });
  map.addLayer({ id: clusterLayer, type: "circle", source: fleetSource, filter: ["has", "point_count"], paint: { "circle-radius": ["step", ["get", "point_count"], 20, 10, 24, 50, 29], "circle-color": "#2f567a", "circle-stroke-color": "#ffffff", "circle-stroke-width": 3, "circle-opacity": .96 } });
  map.addLayer({ id: "fleet-cluster-count", type: "symbol", source: fleetSource, filter: ["has", "point_count"], layout: { "text-field": ["get", "point_count_abbreviated"], "text-font": ["Noto Sans Regular"], "text-size": 13, "text-allow-overlap": true }, paint: { "text-color": "#fff" } });
  map.addLayer({ id: "fleet-selected-ring", type: "circle", source: fleetSource, filter: ["all", ["!", ["has", "point_count"]], ["==", ["get", "selected"], true]], paint: { "circle-radius": 21, "circle-color": "#3576bd", "circle-opacity": .13, "circle-stroke-color": "#3576bd", "circle-stroke-width": 1.5, "circle-stroke-opacity": .6 } });
  map.addLayer({ id: vehicleLayer, type: "symbol", source: fleetSource, filter: ["!", ["has", "point_count"]], layout: { "icon-image": ["concat", "fleet-truck-", ["get", "status"]], "icon-size": 1.4, "icon-rotate": ["get", "heading"], "icon-rotation-alignment": "map", "icon-pitch-alignment": "map", "icon-allow-overlap": true, "icon-ignore-placement": true } });
  map.addLayer({ id: modelLayer, type: "fill-extrusion", source: modelSource, paint: { "fill-extrusion-color": ["get", "color"], "fill-extrusion-height": ["get", "height"], "fill-extrusion-base": ["get", "base"], "fill-extrusion-opacity": 1, "fill-extrusion-vertical-gradient": true } });
  map.addLayer({ id: plateLayer, type: "symbol", source: fleetSource,
    filter:["!",["has","point_count"]],
    layout: {"text-field":["get","verticalPlate"],"text-font":["Noto Sans Bold"],"text-size":plateTextSize,"text-line-height":plateLineHeight,
      "icon-image":"fleet-number-banner","icon-text-fit":"both","icon-text-fit-padding":[4,3,4,3],
      "icon-pitch-alignment":"viewport","icon-rotation-alignment":"viewport",
      "text-anchor":"bottom","text-offset":["get","bannerOffset"],"text-allow-overlap":true,"text-ignore-placement":true,"icon-allow-overlap":true,"icon-ignore-placement":true,"text-padding":3,"text-max-width":20,
      "text-pitch-alignment":"viewport","text-rotation-alignment":"viewport","symbol-sort-key":["get","priority"]},
    paint:{"icon-opacity":.42,"text-opacity":1,"text-color":["get","color"],"text-halo-width":0} });
  map.addLayer({id:"fleet-tail-battery",type:"symbol",source:fleetSource,filter:["!",["has","point_count"]],
    layout:{"text-field":["get","batteryLabel"],"text-font":["Noto Sans Bold"],"text-size":batteryTextSize,
      "text-offset":["get","batteryOffset"],"text-anchor":"bottom","text-allow-overlap":true,"text-ignore-placement":true,
      "icon-image":"fleet-battery-badge","icon-text-fit":"both","icon-text-fit-padding":[3,4,3,4],"icon-allow-overlap":true,"icon-ignore-placement":true,
      "text-pitch-alignment":"viewport","text-rotation-alignment":"viewport","icon-pitch-alignment":"viewport","icon-rotation-alignment":"viewport"},
    paint:{"text-color":["get","batteryColor"],"text-opacity":1}});
  // Only styles with OpenMapTiles building data can provide real extrusions.
  if (map.getSource("openmaptiles")) {
    map.addLayer({ id: "fleet-buildings-3d", source: "openmaptiles", "source-layer": "building", type: "fill-extrusion", minzoom: 14,
      filter: ["!=", ["get", "hide_3d"], true], paint: { "fill-extrusion-color": "#c4cdd4", "fill-extrusion-height": ["coalesce", ["get", "render_height"], 0], "fill-extrusion-base": ["coalesce", ["get", "render_min_height"], 0], "fill-extrusion-opacity": .55 } }, "fleet-road-casing");
  }
}
